import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '@dru/api-client';
import { EXAM_TYPE_LABEL, FITNESS_LABEL } from '@dru/types';
import type {
  CursorPage, HealthCardStatus, HealthReportCard, HealthReportList, HealthReportPanel,
  HealthReportSummary, HealthReportView,
} from '@dru/types';
import {
  Alert, Button, Card, CardHeader, DataTable, Dialog, EmptyState, ErrorState, IconAction, RowActions,
  SearchInput, StatusBadge, TextSkeleton, cn, fitnessTone, type Tone,
} from '@dru/ui';
import {
  ArrowDown, ArrowUp, Check, ChevronDown, Download, Eye, FileBadge, Lock, TriangleAlert,
} from 'lucide-react';
import { api } from '../../../lib/api';
import { useAuth, errorMessage } from '../../../lib/auth';
import { useQuery } from '../../../lib/useQuery';
import { formatDate } from '../../../lib/format';
import { openReport } from '../../lab/routes/Reports';

/**
 * The worker's Health Check Report, as HR sees it.
 *
 * Two gates decide what this renders, and both are the server's: the role
 * holds `report.health-read`, and the company's own policy — FULL, SUMMARY
 * or NONE — set on the Settings screen and told to the worker in the consent
 * notice. Under SUMMARY the payload comes back null and only the counts and
 * the fitness decision are shown; under NONE the list itself is refused and
 * this says so plainly. Nothing here reads a value the server did not send,
 * and no flag is computed here: Normal / Low / High / Critical are the
 * card's own `status`. Every open is audited on the server.
 */

const day = (d?: string | null) => formatDate(d);

/** The result flags, in plain words. The status itself is the server's. */
const RESULT: Record<HealthCardStatus, { label: string; tone: Tone; icon?: ReactNode }> = {
  IN_RANGE: { label: 'Normal', tone: 'covered', icon: <Check aria-hidden className="h-3 w-3" /> },
  LOW: { label: 'Low', tone: 'dueSoon', icon: <ArrowDown aria-hidden className="h-3 w-3" /> },
  HIGH: { label: 'High', tone: 'dueSoon', icon: <ArrowUp aria-hidden className="h-3 w-3" /> },
  CRITICAL: { label: 'Critical', tone: 'overdue', icon: <TriangleAlert aria-hidden className="h-3 w-3" /> },
  ABNORMAL: { label: 'Abnormal', tone: 'dueSoon' },
  NO_RANGE: { label: 'No range', tone: 'neutral' },
  TEXT: { label: 'Recorded', tone: 'neutral' },
};
const FLAGGED = new Set<HealthCardStatus>(['LOW', 'HIGH', 'CRITICAL', 'ABNORMAL']);

/** Where a report stands as a document: ready, still rendering, failed, or replaced. */
function reportState(r: Pick<HealthReportSummary, 'status' | 'generationStatus'>): { label: string; tone: Tone } {
  if (r.status === 'AMENDED') return { label: 'Superseded', tone: 'neutral' };
  if (r.generationStatus === 'PENDING') return { label: 'Preparing PDF', tone: 'pending' };
  if (r.generationStatus === 'GENERATION_FAILED') return { label: 'PDF unavailable', tone: 'dueSoon' };
  return { label: 'Ready', tone: 'covered' };
}

/** The policy refusal, told apart from a real failure so it reads as a rule, not an error. */
export function isPolicyRefusal(e: unknown) {
  return e instanceof ApiError && e.type === 'health-report-policy';
}
type Listed = HealthReportList & { refused?: string };
function fetchList(query: string): Promise<Listed> {
  return api.get<HealthReportList>(`/health-reports?${query}`).catch((e: unknown) => {
    if (isPolicyRefusal(e)) return { policy: 'NONE', items: [], refused: errorMessage(e) } as Listed;
    throw e;
  });
}

export function NotShared({ message }: { message?: string }) {
  return (
    <EmptyState
      icon={<Lock className="h-6 w-6" />}
      title="Health reports are not shared with HR"
      hint={message ?? 'Your company has chosen not to share health check reports with HR. The fitness certificate is still available.'}
    />
  );
}

/** The counts, as a sentence and a bar. */
export function CountsLine({ k }: { k: HealthReportSummary['summary'] }) {
  const out = k.low + k.high + k.critical + k.abnormal;
  return (
    <div>
      <div className="text-xs text-neutral-700">
        <strong className="tabular-nums">{k.inRange}</strong> of <strong className="tabular-nums">{k.withRange}</strong> normal
        {out ? <> · <span className={cn('font-medium', k.critical ? 'text-unfit-600' : 'text-restricted-600')}>{out} outside range{k.critical ? ` (${k.critical} critical)` : ''}</span></> : null}
        {k.noRange ? ` · ${k.noRange} without a range` : ''}
      </div>
      <div className="mt-1 flex h-1.5 w-full max-w-xs overflow-hidden rounded bg-neutral-200" aria-hidden>
        {k.withRange > 0 && (
          <>
            <span className="bg-fit-600" style={{ width: `${(k.inRange / k.withRange) * 100}%` }} />
            <span className="bg-restricted-600" style={{ width: `${((k.low + k.high + k.abnormal) / k.withRange) * 100}%` }} />
            <span className="bg-unfit-600" style={{ width: `${(k.critical / k.withRange) * 100}%` }} />
          </>
        )}
      </div>
    </div>
  );
}

/**
 * View / Download PDF / Certificate, each only where it can work: the PDF
 * only under FULL with a rendered document, the certificate only for a role
 * that reads the register and a report that names one.
 */
function useReportActions(r: HealthReportSummary) {
  const go = useNavigate();
  const { permissions } = useAuth();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function pdf(mode: 'view' | 'download') {
    setBusy(mode); setError(null);
    try { await openReport(`/health-reports/${r.id}/download`, mode); }
    catch (e) { setError(errorMessage(e)); }
    finally { setBusy(null); }
  }

  // The report carries the certificate's number, not its id: find it on the
  // same examination through the register the role can already read.
  async function certificate() {
    setBusy('cert'); setError(null);
    try {
      const page = await api.get<CursorPage<{ id: string; certificateNumber?: string | null }>>(
        `/certificates?orderId=${r.orderId}&limit=10`);
      const hit = page.items.find((c) => c.certificateNumber === r.fitness?.certificateNumber) ?? page.items[0];
      if (!hit) throw new Error('The certificate for this examination could not be found.');
      go(`/hr/certificates/${hit.id}`);
    } catch (e) { setError(errorMessage(e)); setBusy(null); }
  }

  const canCertificate = Boolean(r.fitness?.certificateNumber) && permissions.has('certificate.read');
  return { busy, error, pdf, certificate, canCertificate };
}

function ReportActions({ r, policy, onOpen, opened }: {
  r: HealthReportSummary; policy: HealthReportList['policy']; onOpen: () => void; opened?: boolean;
}) {
  const a = useReportActions(r);
  const pdfReady = policy === 'FULL' && r.hasDocument;
  return (
    <div className="flex flex-col items-start gap-1 sm:items-end">
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant={opened ? 'primary' : 'secondary'} icon={<Eye className="h-4 w-4" />} onClick={onOpen}
                aria-label={`${opened ? 'Hide' : 'View'} report ${r.reportNumber}`} aria-expanded={opened}>
          {opened ? 'Hide report' : policy === 'FULL' ? 'View report' : 'View summary'}
        </Button>
        {pdfReady && (
          <Button size="sm" variant="secondary" icon={<Download className="h-4 w-4" />} loading={a.busy === 'download'}
                  onClick={() => a.pdf('download')} aria-label={`Download report ${r.reportNumber} (PDF)`}>
            PDF
          </Button>
        )}
        {a.canCertificate && (
          <Button size="sm" variant="ghost" icon={<FileBadge className="h-4 w-4" />} loading={a.busy === 'cert'}
                  onClick={a.certificate} aria-label={`View fitness certificate ${r.fitness?.certificateNumber ?? ''}`}>
            Certificate
          </Button>
        )}
      </div>
      {a.error && <p role="alert" className="text-xs text-unfit-600">{a.error}</p>}
    </div>
  );
}

/** The register's row actions: the same three, as icons with names, so a row stays one line. */
function RegisterActions({ r, policy, onOpen }: { r: HealthReportSummary; policy: HealthReportList['policy']; onOpen: () => void }) {
  const a = useReportActions(r);
  return (
    <div className="flex flex-col items-end gap-1">
      <RowActions>
        <IconAction label={policy === 'FULL' ? `View report ${r.reportNumber}` : `View summary of report ${r.reportNumber}`} icon={Eye} onClick={onOpen} />
        {policy === 'FULL' && r.hasDocument && (
          <IconAction label={`Download report ${r.reportNumber} (PDF)`} icon={Download} loading={a.busy === 'download'} onClick={() => a.pdf('download')} />
        )}
        {a.canCertificate && (
          <IconAction label={`View fitness certificate ${r.fitness?.certificateNumber ?? ''}`} icon={FileBadge} loading={a.busy === 'cert'} onClick={a.certificate} />
        )}
      </RowActions>
      {a.error && <p role="alert" className="max-w-[14rem] text-right text-xs text-unfit-600">{a.error}</p>}
    </div>
  );
}

function ReportRow({ r, policy, onOpen, opened }: {
  r: HealthReportSummary; policy: HealthReportList['policy']; onOpen: (id: string) => void; opened: boolean;
}) {
  const st = reportState(r);
  return (
    <li className={cn('px-5 py-4', opened && 'bg-primary-50/40')}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            {r.fitness
              ? <StatusBadge tone={fitnessTone(r.fitness.decision)}>{FITNESS_LABEL[r.fitness.decision]}</StatusBadge>
              : <StatusBadge tone="neutral">No decision</StatusBadge>}
            <StatusBadge tone={st.tone}>{st.label}</StatusBadge>
          </div>
          <div className="text-small text-neutral-800">
            {r.examinationType ? (EXAM_TYPE_LABEL[r.examinationType] ?? r.examinationType) : 'Examination'}
            <span className="text-neutral-500"> · checkup {day(r.attendedAt)}</span>
          </div>
          <div className="text-caption text-neutral-500">
            <span className="font-mono">{r.reportNumber}</span>
            {r.revision > 1 && <> · revision {r.revision}</>}
            {' · '}issued {day(r.issuedAt)}
            {r.fitness?.physician ? ` · ${r.fitness.physician}` : ''}
          </div>
          <CountsLine k={r.summary} />
        </div>
        <ReportActions r={r} policy={policy} opened={opened} onOpen={() => onOpen(r.id)} />
      </div>
    </li>
  );
}

/** The panels of one report, under the body system the server filed them in. */
function bySystem(v: HealthReportView, panels: HealthReportPanel[]) {
  const groups = v.systems.map((s) => ({
    name: s.name, counts: s.counts,
    panels: panels.filter((p) => p.bodySystem === s.name || s.panels.includes(p.name) || s.panels.includes(p.code)),
  }));
  const placed = new Set(groups.flatMap((g) => g.panels.map((p) => p.code)));
  for (const p of panels) {
    if (placed.has(p.code)) continue;
    let g = groups.find((x) => x.name === p.bodySystem);
    if (!g) { g = { name: p.bodySystem || 'Other', counts: p.counts, panels: [] }; groups.push(g); }
    g.panels.push(p);
    placed.add(p.code);
  }
  return groups.filter((g) => g.panels.length > 0);
}

const flaggedIn = (k: { low: number; high: number; critical: number; abnormal: number }) => k.low + k.high + k.critical + k.abnormal;

/** One report on screen: the decision, the counts, and — under FULL — every result by body system. */
export function HealthReportBody({ v }: { v: HealthReportView }) {
  const p = v.payload;
  const f = v.fitness;
  const [onlyFlagged, setOnlyFlagged] = useState(false);
  const groups = useMemo(() => (p ? bySystem(v, p.panels) : []), [v, p]);

  return (
    <div className="space-y-5">
      {f && (
        <section aria-label="Fitness decision" className="rounded-card border border-neutral-200 bg-white p-4">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge tone={fitnessTone(f.decision)}>{f.decisionLabel}</StatusBadge>
            {f.certificateNumber && (
              f.certificateRevoked
                ? <span className="text-small text-unfit-600">Certificate <span className="font-mono">{f.certificateNumber}</span> withdrawn {day(f.certificateRevokedAt)}</span>
                : <span className="text-small text-neutral-700">Certificate <span className="font-mono">{f.certificateNumber}</span> · valid to {day(f.validUntil)}</span>
            )}
          </div>
          {(f.retestDueDate || f.followUpMonths || f.physician) && (
            <div className="mt-1.5 text-caption text-neutral-500">
              {f.physician ? `Signed by ${f.physician}` : ''}
              {f.retestDueDate && <span className="text-restricted-600">{f.physician ? ' · ' : ''}re-test due {day(f.retestDueDate)}</span>}
              {f.followUpMonths ? `${f.physician || f.retestDueDate ? ' · ' : ''}next checkup in ${f.followUpMonths} months` : ''}
            </div>
          )}
          {f.restrictions.length > 0 && (
            <ul className="mt-2 list-disc pl-5 text-small text-neutral-800">
              {f.restrictions.map((r) => <li key={r.code}>{r.label}{r.note ? ` — ${r.note}` : ''}</li>)}
            </ul>
          )}
          {p?.fitness?.advice && <p className="mt-2 whitespace-pre-wrap border-l-2 border-fit-600 pl-3 text-small text-neutral-800">{p.fitness.advice}</p>}
          {p?.fitness?.findings?.length ? (
            <ul className="mt-2 space-y-1 text-small text-neutral-800">
              {p.fitness.findings.map((x, i) => <li key={i}><span className="mr-2 text-caption uppercase tracking-wide text-neutral-500">{x.category}</span>{x.finding}</li>)}
            </ul>
          ) : null}
        </section>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Tile n={v.summary.measured} label={`tests across ${v.summary.panelsTotal} panels`} />
        <Tile n={v.summary.inRange} label={`normal, of ${v.summary.withRange} with a range`} tone="fit" />
        <Tile n={v.flaggedCount} label={`outside range${v.summary.critical ? ` · ${v.summary.critical} critical` : ''}`}
              tone={v.summary.critical ? 'unfit' : v.flaggedCount ? 'restricted' : 'fit'} />
        <Tile n={v.summary.panelsFlagged} label="panels with a flagged result" />
      </div>

      {!p ? (
        <>
          {v.systems.length > 0 && (
            <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {v.systems.map((s) => (
                <li key={s.name} className="rounded-card border border-neutral-200 bg-white px-3 py-2">
                  <div className="text-small font-medium text-neutral-900">{s.name}</div>
                  {s.panels.some((x) => x !== s.name) && <div className="text-caption text-neutral-500">{s.panels.join(' · ')}</div>}
                  <div className="mt-1"><CountsLine k={{ ...s.counts, panelsTotal: 0, panelsFlagged: 0, pctInRange: null }} /></div>
                </li>
              ))}
            </ul>
          )}
          <Alert tone="info">
            Your company shows HR the summary and the fitness decision, not the test values. The full report is the employee's and the physician's; the policy is on the Settings screen.
          </Alert>
        </>
      ) : (
        <section aria-label="Results by body system" className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h4 className="text-h4 text-neutral-900">Results by body system</h4>
            <label className="inline-flex cursor-pointer items-center gap-2 text-small text-neutral-700 max-md:min-h-10">
              <input type="checkbox" checked={onlyFlagged} onChange={(e) => setOnlyFlagged(e.target.checked)}
                     className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500" />
              Only results outside range
            </label>
          </div>
          {groups.map((g) => {
            const flagged = flaggedIn(g.counts);
            const panels = g.panels
              .map((panel) => ({ panel, cards: onlyFlagged ? panel.cards.filter((c) => FLAGGED.has(c.status)) : panel.cards }))
              .filter((x) => x.cards.length > 0);
            if (onlyFlagged && panels.length === 0) return null;
            return (
              // Systems with a flagged result open; the all-normal ones stay folded.
              <details key={g.name} open={flagged > 0 || onlyFlagged} className="group rounded-card border border-neutral-200 bg-white">
                <summary className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-2 px-4 py-3 max-md:min-h-11 [&::-webkit-details-marker]:hidden">
                  <span className="flex items-center gap-2">
                    <ChevronDown aria-hidden className="h-4 w-4 text-neutral-500 transition-transform group-open:rotate-180" />
                    <span className="text-body font-semibold text-neutral-900">{g.name}</span>
                    {g.counts.critical > 0
                      ? <StatusBadge tone="overdue">{g.counts.critical} critical</StatusBadge>
                      : flagged > 0
                        ? <StatusBadge tone="dueSoon">{flagged} outside range</StatusBadge>
                        : <StatusBadge tone="covered">All normal</StatusBadge>}
                  </span>
                  {g.panels.some((x) => x.name !== g.name) && (
                    <span className="text-caption text-neutral-500">{g.panels.map((x) => x.name).join(' · ')}</span>
                  )}
                </summary>
                <div className="space-y-3 border-t border-neutral-100 px-4 py-3">
                  {panels.map(({ panel, cards }) => (
                    <div key={panel.code}>
                      {(g.panels.length > 1 || panel.name !== g.name) && (
                        <div className="mb-1 text-caption font-semibold uppercase tracking-wide text-neutral-500">{panel.name}</div>
                      )}
                      <ul className="divide-y divide-neutral-100">
                        {cards.map((c) => <CardRow key={c.code} c={c} />)}
                      </ul>
                    </div>
                  ))}
                </div>
              </details>
            );
          })}
        </section>
      )}
    </div>
  );
}

function Tile({ n, label, tone }: { n: number; label: string; tone?: 'fit' | 'restricted' | 'unfit' }) {
  return (
    <div className="rounded-card border border-neutral-200 bg-white px-3 py-2">
      <div className={cn('text-h3 font-semibold tabular-nums', tone === 'fit' && 'text-fit-600', tone === 'restricted' && 'text-restricted-600', tone === 'unfit' && 'text-unfit-600')}>{n}</div>
      <div className="text-caption font-normal text-neutral-500">{label}</div>
    </div>
  );
}

function CardRow({ c }: { c: HealthReportCard }) {
  const r = RESULT[c.status];
  return (
    <li className={cn('py-2', c.status === 'CRITICAL' && '-mx-2 rounded bg-unfit-600/5 px-2')}>
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
        <div className="min-w-0 flex-1">
          <div className="text-small text-neutral-900">{c.name}
            {c.technicalName && c.technicalName !== c.name && <span className="ml-2 text-caption text-neutral-500">{c.technicalName}</span>}
          </div>
          <div className="text-caption text-neutral-500">
            {c.rangeText ? `Normal range ${c.rangeText}` : 'No reference range'}
            {c.previous ? ` · last time ${c.previous.value}${c.unit ? ` ${c.unit}` : ''} (${day(c.previous.at)})` : ''}
          </div>
          {c.description && <p className="mt-1 text-caption text-neutral-600">{c.description}</p>}
        </div>
        <div className="flex items-center gap-2">
          <span className={cn('text-small font-medium tabular-nums text-neutral-900', c.valueType === 'TEXT' && 'max-w-xs whitespace-pre-wrap font-normal')}>
            {c.value}{c.unit && c.valueType !== 'TEXT' ? ` ${c.unit}` : ''}
          </span>
          <StatusBadge tone={r.tone}><span className="inline-flex items-center gap-1">{r.icon}{r.label}</span></StatusBadge>
        </div>
      </div>
    </li>
  );
}

/** One report opened from a list: its own fetch, its own loading and error. */
function OpenedReport({ id }: { id: string }) {
  const detail = useQuery<HealthReportView>(() => api.get(`/health-reports/${id}`), [id]);
  if (detail.loading && !detail.data) return <TextSkeleton lines={4} />;
  if (detail.error) return <ErrorState title="The report could not be opened" detail={detail.error} onRetry={detail.reload} />;
  return detail.data ? <HealthReportBody v={detail.data} /> : null;
}

const NO_REPORT_HINT = 'A report is issued when the physician signs the fitness decision.';

/** The report list for one worker, with one open at a time. */
export function HealthReportsTab({ employeeId }: { employeeId: string }) {
  const list = useQuery<Listed>(() => fetchList(`employeeId=${employeeId}&limit=50`), [employeeId]);
  const [openId, setOpenId] = useState<string | null>(null);
  const items = list.data?.items ?? [];
  const opened = items.find((r) => r.id === openId);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="Health check reports"
          hint={list.data?.policy === 'FULL'
            ? 'The employee\'s full report, as the consent notice allows. Every open is recorded.'
            : 'Your company shows HR the summary and the fitness decision only.'}
        />
        {list.error ? <div className="px-5 pb-4"><ErrorState title="Reports could not be loaded" detail={list.error} onRetry={list.reload} /></div>
          : list.loading && !list.data ? <div className="px-5 pb-4"><TextSkeleton lines={3} /></div>
          : list.data?.refused ? <div className="px-5 pb-4"><NotShared message={list.data.refused} /></div>
          : items.length === 0 ? <div className="px-5 pb-4"><EmptyState title="No report yet" hint={NO_REPORT_HINT} /></div>
          : (
            <ul className="divide-y divide-neutral-100 border-t border-neutral-100">
              {items.map((r) => (
                <ReportRow key={r.id} r={r} policy={list.data!.policy} opened={r.id === openId}
                           onOpen={(id) => setOpenId(id === openId ? null : id)} />
              ))}
            </ul>
          )}
      </Card>
      {opened && (
        <Card>
          <CardHeader title={`Report ${opened.reportNumber}`} hint={`Checkup ${day(opened.attendedAt)} · issued ${day(opened.issuedAt)}`} />
          <div className="px-5 pb-5"><OpenedReport id={opened.id} /></div>
        </Card>
      )}
    </div>
  );
}

/** The report on one examination, for the examination detail screen. */
export function HealthReportForOrder({ orderId }: { orderId: string }) {
  const list = useQuery<Listed>(() => fetchList(`orderId=${orderId}&limit=5`), [orderId]);
  const [openId, setOpenId] = useState<string | null>(null);
  if (list.error) return <p className="text-small text-neutral-500">{list.error}</p>;
  if (list.loading && !list.data) return <TextSkeleton lines={2} />;
  if (list.data?.refused) return <p className="text-small text-neutral-500">{list.data.refused}</p>;
  const items = list.data?.items ?? [];
  if (!items.length) return <p className="text-small text-neutral-500">Not issued yet. {NO_REPORT_HINT}</p>;
  return (
    <div className="space-y-3">
      <ul className="-mx-5 divide-y divide-neutral-100">
        {items.map((r) => (
          <ReportRow key={r.id} r={r} policy={list.data!.policy} opened={r.id === openId}
                     onOpen={(id) => setOpenId(id === openId ? null : id)} />
        ))}
      </ul>
      {openId && <OpenedReport id={openId} />}
    </div>
  );
}

/**
 * The company's report register: the latest reports across the plants this
 * login can see, newest first. The server returns at most 100 and has no
 * search of its own, so the search here narrows what is loaded and says so.
 */
export function HealthReportsRegister() {
  const list = useQuery<Listed>(() => fetchList('limit=100'), []);
  const [q, setQ] = useState('');
  const [earlier, setEarlier] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const items = list.data?.items ?? [];
  const needle = q.trim().toLowerCase();
  // A re-issued report keeps its earlier revision, marked Superseded. The
  // current one is what HR acts on; the history is a tick away.
  const superseded = items.filter((r) => r.status === 'AMENDED').length;
  const rows = items
    .filter((r) => earlier || r.status !== 'AMENDED')
    .filter((r) => !needle || [r.employee?.fullName, r.employee?.employeeCode, r.reportNumber]
      .some((s) => s?.toLowerCase().includes(needle)));
  const opened = items.find((r) => r.id === openId);
  const policy = list.data?.policy ?? 'SUMMARY';

  if (list.data?.refused) return <Card><div className="p-5"><NotShared message={list.data.refused} /></div></Card>;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SearchInput value={q} onValueChange={setQ} placeholder="Search name, employee ID or report number"
                     className="w-full sm:max-w-sm" aria-label="Search health reports" />
        {list.data && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-small text-neutral-500">
            {superseded > 0 && (
              <label className="inline-flex cursor-pointer items-center gap-2 text-neutral-700 max-md:min-h-10">
                <input type="checkbox" checked={earlier} onChange={(e) => setEarlier(e.target.checked)}
                       className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500" />
                Show earlier revisions ({superseded})
              </label>
            )}
            <span>
              {rows.length} shown{items.length === 100 ? ' · latest 100 reports' : ''}
              {policy === 'SUMMARY' && ' · summary only'}
            </span>
          </div>
        )}
      </div>
      <DataTable<HealthReportSummary>
        rows={rows}
        loading={list.loading}
        error={list.error}
        onRetry={list.reload}
        rowKey={(r) => r.id}
        onRowClick={(r) => setOpenId(r.id)}
        empty={needle
          ? { title: 'No report matches', hint: 'Try a name, an employee ID or a report number.' }
          : { title: 'No health reports yet', hint: NO_REPORT_HINT }}
        columns={[
          {
            key: 'who', header: 'Employee', alwaysVisible: true,
            cell: (r) => (
              <div className="min-w-0">
                <div className="truncate font-medium text-neutral-900">{r.employee?.fullName ?? '—'}</div>
                <div className="font-mono text-caption text-neutral-500">{r.employee?.employeeCode ?? '—'}</div>
              </div>
            ),
          },
          { key: 'date', header: 'Checkup date', cell: (r) => <span className="whitespace-nowrap">{day(r.attendedAt)}</span> },
          {
            key: 'number', header: 'Report no.',
            cell: (r) => (
              <span className="whitespace-nowrap font-mono text-small text-neutral-700">
                {r.reportNumber}{r.revision > 1 && <span className="ml-1 font-sans text-caption text-neutral-500">rev {r.revision}</span>}
              </span>
            ),
          },
          {
            key: 'fitness', header: 'Fitness', mobileLabel: false,
            cell: (r) => (r.fitness
              ? <StatusBadge tone={fitnessTone(r.fitness.decision)}>{FITNESS_LABEL[r.fitness.decision]}</StatusBadge>
              : <span className="text-neutral-500">—</span>),
          },
          {
            key: 'status', header: 'Report', mobileLabel: false,
            cell: (r) => { const st = reportState(r); return <StatusBadge tone={st.tone}>{st.label}</StatusBadge>; },
          },
          {
            key: 'actions', header: 'Actions', align: 'right', alwaysVisible: true, mobileLabel: '',
            cell: (r) => (
              <div onClick={(e) => e.stopPropagation()}>
                <RegisterActions r={r} policy={policy} onOpen={() => setOpenId(r.id)} />
              </div>
            ),
          },
        ]}
      />
      {opened && (
        <Dialog wide title={`${opened.employee?.fullName ?? 'Health report'} · ${opened.reportNumber}`} onClose={() => setOpenId(null)}>
          <OpenedReport id={opened.id} />
        </Dialog>
      )}
    </div>
  );
}
